import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { IVacationAwardingResponse, IVacationAwardingSavePayload, VacationBidRound } from "@vmosolutions/oc-interfaces";

import {
  dismissVacationAwardWarning,
  getVacationAwarding,
  publishVacationAwarding,
  saveVacationAwarding,
} from "../helpers";
import {
  IPendingChange,
  applySaveResponse,
  createSerialQueue,
  openWarnings,
  pendingChangesOf,
} from "./awardingHelpers";

export type AwardingChanges = Omit<IVacationAwardingSavePayload, "round">;

export interface IVacationAwarding {
  /** Null until the first load for this package lands. */
  view: IVacationAwardingResponse | null;
  loading: boolean;
  /** Changes asked for whose saves have not landed yet, in the order they were made. */
  pending: IPendingChange[];
  /** True while any save is queued or running, or a publish is in flight. */
  saving: boolean;
  load: () => Promise<void>;
  /**
   * Queues a change and resolves true once it is stored and folded into `view`. Saves reach the
   * server one at a time, in the order they were made, so a refused one fails on its own.
   */
  save: (changes: AwardingChanges) => Promise<boolean>;
  publish: () => Promise<boolean>;
  /** Takes an entitlement change warning off a crew member, and resolves true once it is gone from `view`. */
  dismissWarning: (userId: string, warningId: string) => Promise<boolean>;
}

/**
 * The awarding view for one package and round, shared by the View Bids and Award Bids tabs. Loaded
 * on demand, and kept current by folding in each save's response rather than reloading.
 */
export const useVacationAwarding = (packageId: string | undefined, round: VacationBidRound): IVacationAwarding => {
  const dispatch = useDispatch();

  const [view, setView] = useState<IVacationAwardingResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [pending, setPending] = useState<IPendingChange[]>([]);
  const [publishing, setPublishing] = useState<boolean>(false);

  // A response for a package or round the editor has since moved off must not land in the new view.
  const packageRef = useRef<string | undefined>(packageId);
  packageRef.current = packageId;
  const roundRef = useRef<VacationBidRound>(round);
  roundRef.current = round;

  // True once a tab has asked for this package's view. The view is fetched on demand, so nothing is
  // loaded until one is opened - but after that a round change refetches on its own.
  const requestedRef = useRef<boolean>(false);

  const [syncedPackageId, setSyncedPackageId] = useState<string | undefined>(packageId);
  const [syncedRound, setSyncedRound] = useState<VacationBidRound>(round);
  if (packageId !== syncedPackageId || round !== syncedRound) {
    if (packageId !== syncedPackageId) requestedRef.current = false;
    setSyncedPackageId(packageId);
    setSyncedRound(round);
    setView(null);
    setPending([]);
  }

  // One queue for the life of the editor, so every save from it waits its turn behind the last.
  const queueRef = useRef<ReturnType<typeof createSerialQueue> | null>(null);
  if (!queueRef.current) queueRef.current = createSerialQueue();
  const enqueue = queueRef.current;
  const keyRef = useRef<number>(0);
  // Counts landed saves, so a load that was read before one of them landed is not applied over it.
  const savesLandedRef = useRef<number>(0);
  // Counts loads started. Leaving a round and coming back leaves two in flight for the same round,
  // which the package and round checks cannot tell apart, so only the newest may land or stop the
  // spinner.
  const loadsStartedRef = useRef<number>(0);

  const load = useCallback(async (): Promise<void> => {
    if (!packageId) return;
    requestedRef.current = true;
    const loadNumber = (loadsStartedRef.current += 1);
    const savesLandedAtStart = savesLandedRef.current;
    setLoading(true);
    try {
      const response = await getVacationAwarding(packageId, round, {
        dispatch,
        errorMessage: "Unable to load the package's bids",
      });
      if (
        response &&
        loadsStartedRef.current === loadNumber &&
        packageRef.current === packageId &&
        roundRef.current === round &&
        savesLandedRef.current === savesLandedAtStart
      ) {
        setView(response);
      }
    } finally {
      if (loadsStartedRef.current === loadNumber) setLoading(false);
    }
  }, [dispatch, packageId, round]);

  // `load` is remade when the round changes, which is the moment the emptied view needs refilling.
  useEffect(() => {
    if (requestedRef.current) void load();
  }, [load]);

  const save = useCallback(
    (changes: AwardingChanges): Promise<boolean> => {
      if (!packageId) return Promise.resolve(false);
      const changesPending = pendingChangesOf(changes, () => (keyRef.current += 1));
      const keys = new Set(changesPending.map((z) => z.key));
      setPending((current) => [...current, ...changesPending]);

      return enqueue(async (): Promise<boolean> => {
        try {
          const response = await saveVacationAwarding(
            packageId,
            { round, ...changes },
            { dispatch, errorMessage: "Unable to save the change" }
          );
          if (packageRef.current !== packageId || roundRef.current !== round) return false;
          if (!response) {
            // Reloaded, so a round published, closed or disabled elsewhere locks this screen too.
            void load();
            return false;
          }
          savesLandedRef.current += 1;
          setView((current) => (current ? applySaveResponse(current, response) : current));
          return true;
        } catch {
          return false;
        } finally {
          setPending((current) => current.filter((z) => !keys.has(z.key)));
        }
      });
    },
    [dispatch, packageId, round, enqueue, load]
  );

  const publish = useCallback(async (): Promise<boolean> => {
    if (!packageId || pending.length || publishing) return false;
    setPublishing(true);
    try {
      const response = await publishVacationAwarding(packageId, round, {
        dispatch,
        successMessage: `Round ${round} awards published`,
        errorMessage: "Unable to publish the awards",
      });
      if (!response) return false;
    } finally {
      setPublishing(false);
    }
    // Reloaded rather than patched: publishing changes what the server allows, not just the awards.
    await load();
    return true;
  }, [dispatch, packageId, round, load, pending.length, publishing]);

  const dismissWarning = useCallback(
    async (userId: string, warningId: string): Promise<boolean> => {
      if (!packageId) return false;
      const dismissed = await dismissVacationAwardWarning(packageId, userId, warningId, {
        dispatch,
        errorMessage: "Unable to dismiss the warning",
      });
      if (!dismissed || packageRef.current !== packageId) return false;
      setView((current) =>
        current
          ? {
              ...current,
              crew: current.crew.map((member) =>
                member.userId === userId
                  ? { ...member, warnings: openWarnings(member).filter((z) => String(z._id) !== warningId) }
                  : member
              ),
            }
          : current
      );
      return true;
    },
    [dispatch, packageId]
  );

  return { view, loading, pending, saving: pending.length > 0 || publishing, load, save, publish, dismissWarning };
};
